import {
  DestroyRef,
  ErrorHandler,
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { AuthService } from '@frontendlib/common/services/auth.service';
import * as Sentry from '@sentry/angular';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { AppErrorHandler } from './app-error-handler';
import { ChunkLoadRecoveryService } from './chunk-load-recovery.service';

/**
 * Error reporting for the main app, provided once: the ErrorHandler, recovery from a lazy route whose chunk is gone
 * after a deploy, and the signed-in account on every Sentry event (its id only, never an email or a name).
 */
export function provideErrorReporting(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideEnvironmentInitializer(() => {
      const chunkLoads = inject(ChunkLoadRecoveryService);
      const navigationErrors = inject(Router)
        .events.pipe(filter((event): event is NavigationError => event instanceof NavigationError))
        .subscribe((event) => chunkLoads.recover(event.error, event.url));
      const accounts = inject(AuthService)
        .getAuthenticated()
        .pipe(map((payload) => payload?.accountId ?? null))
        .pipe(distinctUntilChanged())
        .subscribe((accountId) => Sentry.setUser(accountId ? { id: accountId } : null));
      inject(DestroyRef).onDestroy(() => {
        navigationErrors.unsubscribe();
        accounts.unsubscribe();
      });
    }),
  ]);
}
